import { formatDuration, type RecordingSummary, type SourceStatus } from '@greenroom/shared'
import { Film } from 'lucide-react'
import { Link } from 'react-router'
import { formatDateTime, listNames } from '../lib/format'
import { Badge, EmptyState } from './ui/misc'
import { Spinner } from './ui/spinner'

type MediaStatus = RecordingSummary['mediaStatus']

const MEDIA_BADGE: Record<MediaStatus, { tone: 'neutral' | 'accent' | 'ok' | 'warn' | 'danger'; label: string }> = {
  none: { tone: 'neutral', label: 'No media' },
  waiting: { tone: 'warn', label: 'Uploading' },
  processing: { tone: 'accent', label: 'Processing' },
  ready: { tone: 'ok', label: 'Ready' },
  partial: { tone: 'warn', label: 'Partly ready' },
  failed: { tone: 'danger', label: 'Failed' },
}

export function MediaStatusBadge({ status }: { status: MediaStatus | SourceStatus }) {
  const b = MEDIA_BADGE[status]
  return (
    <Badge tone={b.tone}>
      {(status === 'waiting' || status === 'processing') && <Spinner className="size-3 text-current" />}
      {b.label}
    </Badge>
  )
}

export function RecordingStatusBadge({ recording }: { recording: Pick<RecordingSummary, 'status' | 'mediaStatus'> }) {
  if (recording.status === 'recording')
    return (
      <Badge tone="rec">
        <span className="size-1.5 animate-pulse-rec rounded-full bg-rec" />
        Recording
      </Badge>
    )
  if (recording.status === 'paused') return <Badge tone="warn">Paused</Badge>
  return <MediaStatusBadge status={recording.mediaStatus} />
}

export const recordingTitle = (r: Pick<RecordingSummary, 'title' | 'studioName' | 'startedAt'>) => r.title || `${r.studioName} session`

export function RecordingList({ recordings, showStudio }: { recordings: RecordingSummary[]; showStudio?: boolean }) {
  if (recordings.length === 0) {
    return (
      <EmptyState icon={<Film />} title="No recordings yet">
        Recordings appear here as soon as a producer presses record in a studio.
      </EmptyState>
    )
  }
  return (
    <div className="card overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs font-medium tracking-wide text-faint uppercase">
            <th className="px-4 py-3 font-medium">Recording</th>
            {showStudio && <th className="hidden px-4 py-3 font-medium lg:table-cell">Studio</th>}
            <th className="hidden px-4 py-3 font-medium md:table-cell">Length</th>
            <th className="hidden px-4 py-3 font-medium lg:table-cell">People</th>
            <th className="px-4 py-3 text-right font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {recordings.map((r) => (
            <tr key={r.id} className="relative border-b border-border transition-colors last:border-0 hover:bg-surface-2">
              <td className="px-4 py-3.5">
                {/* the stretched link makes the whole row clickable while keeping one real link per row */}
                <Link to={`/recordings/${r.id}`} className="font-medium after:absolute after:inset-0">
                  {recordingTitle(r)}
                </Link>
                <div className="mt-0.5 text-xs text-faint">
                  {formatDateTime(r.startedAt)}
                  {r.takeCount > 1 && ` · ${r.takeCount} takes`}
                </div>
              </td>
              {showStudio && <td className="hidden px-4 py-3.5 text-muted lg:table-cell">{r.studioName}</td>}
              <td className="hidden px-4 py-3.5 font-mono text-[13px] text-muted tabular-nums md:table-cell">{formatDuration(r.durationMs)}</td>
              <td className="hidden max-w-64 truncate px-4 py-3.5 text-muted lg:table-cell">{listNames(r.participantNames) || '–'}</td>
              <td className="px-4 py-3.5 text-right">
                <RecordingStatusBadge recording={r} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
